import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import GridOverlay from './GridOverlay';

/**
 * HeroCarousel - full-bleed home carousel.
 *
 * • Crossfade only (no horizontal slide) - 400ms
 * • Auto-advance every 7s; pauses on hover, focus-within, or `paused` toggle
 * • Keyboard ←/→ to navigate, Space to pause/play
 * • Respects prefers-reduced-motion (instant swap, no auto-advance)
 * • Preloads the first slide image (LCP)
 *
 * H1 SEO note: only the FIRST slide renders the headline as <h1> for
 * search consistency. Subsequent slides render headline as <h2>.
 */
export const HeroCarousel = ({ slides = [] }) => {
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const [hover, setHover] = useState(false);
    const [focused, setFocused] = useState(false);
    const reduced = useRef(false);
    const pausedRef = useRef(false);
    const rootRef = useRef(null);
    const touchStartX = useRef(null);

    // ref-track combined pause state so the interval tick reads fresh value
    useEffect(() => {
        pausedRef.current = paused || hover || focused;
    }, [paused, hover, focused]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const m = window.matchMedia('(prefers-reduced-motion: reduce)');
        const sync = () => {
            reduced.current = !!m.matches;
        };
        sync();
        m.addEventListener?.('change', sync);
        return () => m.removeEventListener?.('change', sync);
    }, []);

    useEffect(() => {
        slides.forEach((s) => {
            if (!s?.image_url) return;
            const img = new Image();
            img.src = s.image_url;
        });
    }, [slides]);

    const advance = useCallback(
        (delta) => {
            if (!slides.length) return;
            setIdx((i) => (i + delta + slides.length) % slides.length);
        },
        [slides.length]
    );

    // Single long-lived interval; tick checks pausedRef each cycle.
    useEffect(() => {
        if (!slides.length || reduced.current) return;
        const id = setInterval(() => {
            if (pausedRef.current) return;
            setIdx((i) => (i + 1) % slides.length);
        }, 7000);
        return () => clearInterval(id);
    }, [slides.length]);

    const onKey = (e) => {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            advance(-1);
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            advance(1);
        } else if (e.key === ' ' || e.code === 'Space') {
            e.preventDefault();
            setPaused((p) => !p);
        }
    };

    // Touch swipe (mobile) - threshold 50px.
    const onTouchStart = (e) => {
        touchStartX.current = e.touches[0]?.clientX ?? null;
    };
    const onTouchEnd = (e) => {
        if (touchStartX.current == null) return;
        const dx = (e.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
        if (Math.abs(dx) > 50) advance(dx < 0 ? 1 : -1);
        touchStartX.current = null;
    };

    if (!slides.length) return null;

    return (
        <section
            ref={rootRef}
            className="relative w-full overflow-hidden bg-brand-surface h-[520px] md:h-[640px] outline-none"
            tabIndex={0}
            role="region"
            aria-roledescription="carousel"
            aria-label="Centurion home hero"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={onKey}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            data-testid="hero-carousel"
        >
            {slides.map((s, i) => (
                <SlideLayer key={s.id || i} slide={s} active={i === idx} isPrimary={i === 0} />
            ))}

            {/* Pagination */}
            <div
                className="absolute inset-x-0 bottom-6 z-20 flex items-center justify-center gap-2"
                data-testid="hero-pagination"
            >
                {slides.map((s, i) => (
                    <button
                        key={`dot-${s.id || i}`}
                        type="button"
                        aria-label={`Go to slide ${i + 1}`}
                        aria-current={i === idx}
                        onClick={() => setIdx(i)}
                        data-testid={`hero-dot-${i}`}
                        className={[
                            'h-1.5 rounded-sm border outline-none transition-all duration-200',
                            'focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-base',
                            i === idx
                                ? 'w-8 bg-brand-gold border-brand-gold'
                                : 'w-5 bg-transparent border-brand-hairline hover:border-brand-text',
                        ].join(' ')}
                    />
                ))}
                <button
                    type="button"
                    onClick={() => setPaused((p) => !p)}
                    aria-label={paused ? 'Play carousel' : 'Pause carousel'}
                    data-testid="hero-playpause"
                    className="ml-3 inline-flex h-6 w-6 items-center justify-center border border-brand-hairline text-brand-muted hover:border-brand-gold hover:text-brand-gold"
                >
                    {paused ? <Play size={10} /> : <Pause size={10} />}
                </button>
            </div>

            {/* Prev / next chevrons (desktop only, on hover) */}
            <button
                type="button"
                onClick={() => advance(-1)}
                aria-label="Previous slide"
                data-testid="hero-prev"
                className="absolute left-3 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center border border-brand-hairline bg-brand-base/40 text-brand-text opacity-0 backdrop-blur-md transition-opacity duration-200 hover:bg-brand-base/70 group-hover:opacity-100 md:inline-flex"
                style={{ opacity: hover ? 1 : 0 }}
            >
                <ChevronLeft size={20} />
            </button>
            <button
                type="button"
                onClick={() => advance(1)}
                aria-label="Next slide"
                data-testid="hero-next"
                className="absolute right-3 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center border border-brand-hairline bg-brand-base/40 text-brand-text opacity-0 backdrop-blur-md transition-opacity duration-200 hover:bg-brand-base/70 group-hover:opacity-100 md:inline-flex"
                style={{ opacity: hover ? 1 : 0 }}
            >
                <ChevronRight size={20} />
            </button>
        </section>
    );
};

const SlideLayer = ({ slide, active, isPrimary }) => {
    const Heading = isPrimary ? 'h1' : 'h2';
    return (
        <div
            className="absolute inset-0 transition-opacity duration-[400ms] ease-out motion-reduce:transition-none"
            style={{ opacity: active ? 1 : 0 }}
            aria-hidden={!active}
            data-testid={`hero-slide-${slide.sort_order ?? ''}`}
        >
            {slide.image_url ? (
                <img
                    src={slide.image_url}
                    alt=""
                    loading={isPrimary ? 'eager' : 'lazy'}
                    fetchPriority={isPrimary ? 'high' : 'auto'}
                    className="absolute inset-0 h-full w-full object-cover"
                />
            ) : null}
            <div
                className="absolute inset-0"
                style={{
                    background: 'linear-gradient(to bottom, rgba(11,11,13,0.55) 0%, rgba(11,11,13,0.35) 25%, rgba(11,11,13,0.75) 55%, rgba(11,11,13,0.92) 100%)',
                    opacity: 1,
                }}
            />
            <GridOverlay size={56} opacity={0.14} />
            <div
                className="pointer-events-none absolute inset-0"
                style={{ background: 'radial-gradient(ellipse at 30% 30%, rgba(212,162,43,0.06), transparent 55%)' }}
            />

            <div
                className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-16 md:px-10 md:pb-20"
                style={{ textShadow: '0 2px 8px rgba(0,0,0,0.75), 0 0 2px rgba(0,0,0,0.5)' }}
            >
                {slide.eyebrow ? (
                    <span className="eyebrow mb-5">{slide.eyebrow}</span>
                ) : null}
                <Heading className="h-display text-4xl sm:text-5xl lg:text-6xl max-w-3xl font-extrabold">
                    {slide.headline_main}
                    {slide.headline_accent ? (
                        <>
                            {' '}
                            <span className="text-brand-gold">{slide.headline_accent}</span>
                        </>
                    ) : null}
                </Heading>
                {slide.subtitle ? (
                    <p className="mt-5 max-w-2xl text-base md:text-lg font-medium text-brand-text">
                        {slide.subtitle}
                    </p>
                ) : null}
                {(slide.cta_primary_label || slide.cta_secondary_label) && (
                    <div className="mt-8 flex flex-wrap gap-3">
                        {slide.cta_primary_label ? (
                            <CtaLink href={slide.cta_primary_href} primary>
                                {slide.cta_primary_label}
                            </CtaLink>
                        ) : null}
                        {slide.cta_secondary_label ? (
                            <CtaLink href={slide.cta_secondary_href}>
                                {slide.cta_secondary_label}
                            </CtaLink>
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    );
};

const CtaLink = ({ href = '#', primary = false, children }) => {
    const cls = primary
        ? 'bg-brand-gold px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-base transition-colors duration-150 hover:bg-[#E5B647]'
        : 'border border-brand-hairline px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-text transition-colors duration-150 hover:border-brand-gold hover:text-brand-gold';
    if (href?.startsWith('#') || href?.startsWith('http')) {
        return (
            <a href={href} className={cls}>
                {children}
            </a>
        );
    }
    return (
        <Link to={href} className={cls}>
            {children}
        </Link>
    );
};

export default HeroCarousel;
